import type { DocumentHandle } from '@/models/DocumentHandle';
import { Expose, Transform, Type } from 'class-transformer';
import { DocumentSnapshot } from 'firebase/firestore';
import { _FirebaseDocument, anyToDate } from './Models';
import type { ReviewReturned } from './assessmentViews';
import { printableCategory } from '@/utils/requirementCode';

export type TaskReviewDecision = 'pending' | 'accepted' | 'rejected' | 'directive_rejected'
// 'cancelled' is a legacy state — no longer produced, but may exist in historical data
export type TaskProgressStatus = 'todo' | 'in_progress' | 'done' | 'cancelled'

/**
 * A task's progress, with an unset one read as To do — the backend's own rule.
 *
 * **Blank counts as unset, not just `null`/`undefined`.** The field used to be
 * `str = ""` server-side, so legacy documents carry `review_progress: ""`. The
 * backend validator (`project_task.py`, `_unusable_review_state_is_unset`)
 * turns a blank string into `None`, which every reader shows as To do. `??`
 * alone let `""` through: the board dropped those tasks from every column, and
 * the global list printed the raw i18n key `task.progress.` (MYAC-1268 review).
 *
 * Only blank is forgiven. Any other unexpected value is returned as-is, as the
 * backend does — it is not quietly relabelled To do, which would be a claim.
 */
export function progressOrTodo(value: string | null | undefined): TaskProgressStatus {
    if (value == null || !value.trim()) return 'todo'
    return value as TaskProgressStatus
}
export type TaskSchemaVersion = '1' | '2'

export class V2Context {
    @Expose()
    public finding: string
    @Expose()
    public risk: string
}

export class V2Clause {
    @Expose()
    public number: string
    @Expose()
    public title: string
    @Expose()
    public description?: string
    @Expose()
    public standard?: string
}

export class V2DocumentAction {
    /** Optional since 4.8 (CMS `ActionPlanV2DocumentAction.title` is
     * `str | None`): an accepted action-plan item carries ONE string per
     * action, and a hand-authored action has no title either. Declared
     * required here, it forced `as V2Document` casts at every site that
     * builds an action without one (review #528). */
    @Expose()
    public title?: string
    @Expose()
    public description: string
    @Expose()
    public suggested_content?: string
}

export class V2Document {
    @Expose()
    public name: string
    @Expose()
    public action_type: string
    @Expose()
    @Type(() => V2DocumentAction)
    public actions: V2DocumentAction[]
    @Expose()
    public validated?: boolean
    /** MYAC-1504: where the existing document lives, copied from the action
     * plan at accept; absent on tasks accepted before. */
    @Expose()
    public document_handle?: DocumentHandle | null
}

/**
 * Where an engine-created task came from — `ProjectTask.source`, stamped once at
 * genesis and never revised (MYAC-776 R27).
 *
 * Written verbatim by the creating engine, so this is a tolerant reader: every
 * field optional, `track` a plain string. The gap analysis writes
 * `{track: "gap_analysis", gap_id, impact_identities}`
 * (`services/gap_analysis/api.create_tasks_from_workitems`); another track is
 * free to write its own shape without this build refusing to parse the task.
 */
export interface TaskSource {
    /** Which engine produced the task. `gap_analysis` today. */
    track?: string
    /** The analysis it came from — the board's `?gap=` filter matches on it. */
    gap_id?: string
    /** The workitems the task covers. */
    impact_identities?: string[]
    /** The standard the analysis compared, as a person reads it. */
    standard_label?: string
    /** The edition the documentation was written against. */
    prior_label?: string
    /** The edition it must move to. */
    new_label?: string
    /** The clauses this task answers to. Empty on the standard row — see `scope`. */
    clause_codes?: string[]
    /** Where the analysis lives — `/products|qms/<pid>/gap-analysis/<gapId>`. */
    path?: string
    /**
     * Whether this task answers to the standard as a whole (the analysis'
     * synthetic row) or to specific clauses. Absent on tasks written before this
     * field existed — a tolerant reader falls back to `clause_codes` then.
     */
    scope?: 'standard' | 'clause'
    /** The clause keys this task answers to — the analysis' own row identities,
     *  not the redline codes in `clause_codes`. Used to build an exact deep link
     *  back to the row, including the standard row, which carries no code. */
    clause_keys?: string[]
    /**
     * MYAC-776 E-1 — stamped by the engine (additive, a parallel PR) when the
     * analysis this task came from has since been deleted. An ISO timestamp,
     * like every other `_at` field; its presence is what matters here, not its
     * value. Absent on every task whose analysis is still live, and on every
     * task written before this field existed — a tolerant reader falls back to
     * "still live", never the other way round.
     */
    analysis_deleted_at?: string
}

/** The one place the gap analysis's track name is spelled. */
export const TASK_SOURCE_GAP_ANALYSIS = 'gap_analysis'

export class SourceQuestion {
    @Expose()
    public topic_title: string
    @Expose()
    public question_number: number
}

/**
 * A requirement a REFERENTIAL task answers to — MYAC-1093.
 *
 * The other half of "where did this task come from": an assessment task
 * records the grid answers that produced it, a referential task records its
 * anchored requirements, and one Source section renders whichever the task
 * has.
 *
 * `identity` is what OPENS the requirement — the live item's key in this
 * project's adoption, which the requirement dialog resolves against. The uid
 * names the requirement in the referential and cannot be navigated with.
 * Both, plus the label snapshots, are empty when a revision removed the
 * requirement while the work survived (§12.19) — the state the section has to
 * say out loud rather than link into.
 */
export class SourceRequirement {
    @Expose()
    public identity?: string
    @Expose()
    public requirement_uid?: string
    @Expose()
    public code?: string
    @Expose()
    public title?: string
    /** MYAC-1272 review round 2 (additive): the anchor's own display name,
     * present only when `code` is `''` — the same synthetic-obligation case
     * `RemediationAnchor.label` exists for. A chip reads `code || label ||
     * requirement_uid`. */
    @Expose()
    public label?: string

    /**
     * Whether the requirement this entry names is really gone (§12.19).
     *
     * **Not "has no identity".** That was the first reading and it was wrong:
     * `source_requirements` is frozen when the task is accepted, and anchors
     * only started carrying `identity` with MYAC-1093 — so a task accepted
     * from a row projected before that has `code` and `title` and no identity,
     * while the requirement is alive and on screen. Calling that "removed"
     * told the user something false (observed on dev, 2026-08-19).
     *
     * The removed shape is the one the projection actually writes: a uid that
     * resolves to no live item empties the identity, the code AND the title
     * together, because all three are looked up from the same live items. So
     * an entry that kept a label is an entry whose requirement existed — and
     * the only entry with nowhere to go is the one carrying nothing but a uid.
     */
    get isGone(): boolean {
        return !this.identity && !this.code && !this.title
    }
}

export class Task extends _FirebaseDocument {
    @Expose()
    public schema_version: TaskSchemaVersion = '1'
    @Expose()
    public name: string
    @Expose()
    public topic?: string
    @Expose()
    public category?: string
    @Expose()
    public priority: string
    @Expose()
    public description?: string
    @Expose()
    public justification?: string
    @Expose({ name: 'clause' })
    @Type(() => ClauseReference)
    public clauses?: ClauseReference[]
    @Expose({ name: 'document' })
    @Type(() => DocumentUpdateInstruction)
    public documents?: DocumentUpdateInstruction[]

    // Action-First fields (backend-generated, read-only)
    @Expose()
    public finding?: string
    @Expose()
    public risk?: string

    // V2 fields
    @Expose()
    @Type(() => V2Context)
    public context?: V2Context
    @Expose({ name: 'clauses_v2' })
    @Type(() => V2Clause)
    public clausesV2?: V2Clause[]
    @Expose({ name: 'documents_v2' })
    @Type(() => V2Document)
    public documentsV2?: V2Document[]
    @Expose({ name: 'source_questions' })
    @Type(() => SourceQuestion)
    public sourceQuestions?: SourceQuestion[]
    @Expose({ name: 'source_requirements' })
    @Type(() => SourceRequirement)
    public sourceRequirements?: SourceRequirement[]

    // TD-specific fields (null for QMS tasks)
    @Expose()
    public section_key?: string
    @Expose()
    public requirement_id?: string
    @Expose()
    public source_type?: string
    /** MYAC-1166 — stamped by the apply layer once an AI-authored edit landed
     *  (the chat drafting content on the user's behalf). Absent before that,
     *  and on every task written before the field existed. */
    @Expose()
    public ai_edited?: boolean

    /** K4 — the engine provenance the board filters on. Absent on every manual
     *  task and on every task written before the field existed. */
    @Expose()
    public source?: TaskSource

    /** IR-126 §4.3 — see `ProjectTask.proposal_locator`. Carried so the board
     * can tell a manual task from one whose proposal simply has not been
     * resolved yet. */
    @Expose()
    public proposal_locator?: string

    /**
     * `active` · `unapproved` (sent back, kept) · `archived` (terminal).
     *
     * Carried onto the board from the Kanban `ProjectTask` (IR-126 §4.3, 5.3).
     * Until 5.3 it was dropped in `_resolveFromProjectTask`, which was harmless
     * only because MYAC-989 kept non-active tasks off the board entirely — a
     * card that cannot be shown does not need to know its own state.
     *
     * A plain string, not a union, for the same reason `ProjectTask` types it
     * that way: the server may add a state this build has never heard of, and a
     * union would turn that into a parse error on a document we are otherwise
     * happy to render.
     */
    @Expose()
    public lifecycle?: string | null
    /** MYAC-1639 — see `ProjectTask.archived_from`. */
    @Expose()
    public archived_from?: string | null

    // Human review fields (frontend-writable)
    @Expose()
    public review_decision?: TaskReviewDecision
    @Expose()
    public review_assignee_uid?: string | null
    @Expose()
    public review_progress?: TaskProgressStatus
    @Expose()
    public review_rejection_comment?: string | null
    /** MYAC-1639 — see `ProjectTask.review_rejection_reason`. */
    @Expose()
    public review_rejection_reason?: string | null
    /** MYAC-1089 — see `ProjectTask.review_rejected_until`. */
    @Expose()
    @Transform(anyToDate)
    public review_rejected_until?: Date | null
    /** MYAC-1089 — see `ProjectTask.review_returned`. */
    @Expose()
    public review_returned?: ReviewReturned | null

    // Directive matching (backend-generated, read-only)
    @Expose()
    public directive_id?: string | null
    @Expose()
    public directive_match_reason?: string | null
    @Expose()
    public project_task_ref?: string | null
    @Expose()
    public source_assessment_id?: string | null
    @Expose()
    public source_result_id?: string | null
    // Full path of the source copy this task was accepted from (kanban tasks only;
    // used to flip the source doc back to pending on un-accept). See MYAC-719.
    @Expose()
    public source_assessment_ref?: string | null

    get effectiveDecision(): TaskReviewDecision {
        return this.review_decision ?? 'pending'
    }

    get effectiveProgress(): TaskProgressStatus {
        return progressOrTodo(this.review_progress)
    }

    get isAccepted(): boolean {
        return this.effectiveDecision === 'accepted'
    }

    /**
     * Whether this task is live on the board's progress columns.
     *
     * **Absent means `active`**, and that default is the one that matters:
     * phase 0.2 added `lifecycle` with no backfill, so most existing tasks
     * carry nothing. Reading absent as "not active" would sweep every board in
     * the product into the Inactive column on the day this ships.
     *
     * An unrecognised value is deliberately NOT active — it goes to Inactive
     * rather than a progress column. The asymmetry with absent is intentional:
     * absent is a known legacy shape, whereas a value this build does not
     * recognise is a state the server has and we do not, and bucketing it by
     * progress would assert it is being worked on.
     */
    get isActiveLifecycle(): boolean {
        return !this.lifecycle || this.lifecycle === 'active'
    }

    get isArchivedLifecycle(): boolean {
        return this.lifecycle === 'archived'
    }

    get isConsistencyTask(): boolean {
        return this.source_type === 'consistency'
    }

    get isContinuityTask(): boolean {
        return this.source_type === 'continuity'
    }

    /**
     * Whether this task's text was written by a model — AI Act Art. 50(2).
     *
     * Every task that came from a proposal was: the assessment, TD and
     * remediation engines author the name, the description, the finding and
     * risk, and the suggested content of each action. A task somebody typed by
     * hand was not, and labelling that one would be a false claim in the other
     * direction.
     *
     * `source_type` rather than the locator, because it is the one field a
     * manual task is guaranteed to carry — a legacy task has no locator and is
     * engine-written all the same.
     *
     * On the model rather than in a component: the label belongs beside the
     * TITLE, which each dialog owns, so three hosts ask this and one answer
     * keeps them from drifting.
     *
     * MYAC-1166 — a hand-made task is no longer hand-made for good: the chat
     * can draft the content of its actions, and that text renders in the same
     * dialog. The apply layer stamps `ai_edited` on the task document the first
     * time an edit carrying `actor.ai_generated` lands (CMS `apply_edit`), so
     * the title's label follows the content rather than the task's birth.
     */
    get isAiAuthored(): boolean {
        return this.source_type !== 'manual' || this.ai_edited === true
    }

    get isV2(): boolean {
        return this.schema_version === '2'
    }

    // Every surface (card, dialog, report, exports) reads the chip here, so a
    // key never reaches one of them (MYAC-1629).
    get displayTopic(): string {
        return printableCategory(this.isV2 ? this.category : this.topic)
    }

    get displayFinding(): string | null {
        if (this.isV2) {
            return this.context?.finding ?? null
        }
        return this.finding ?? this.justification ?? null
    }

    get displayRisk(): string | null {
        if (this.isV2) {
            return this.context?.risk ?? null
        }
        return this.risk ?? null
    }

    get hasStructuredContext(): boolean {
        if (this.isV2) {
            return !!this.context
        }
        return !!this.finding || !!this.risk
    }

    get isRedactedForFreeTry(): boolean {
        if (this.isV2) {
            return !this.category && !this.context
        }
        return !this.justification?.trim()
    }

    get clauses_v2(): V2Clause[] {
        return this.clausesV2 ?? []
    }

    get documents_v2(): V2Document[] {
        return this.documentsV2 ?? []
    }

    get source_questions(): SourceQuestion[] {
        return this.sourceQuestions ?? []
    }

    get source_requirements(): SourceRequirement[] {
        return this.sourceRequirements ?? []
    }

    collection(): string {
        return "tasks";
    }
    static fromFirebase(o: DocumentSnapshot): Task | null {
        return _FirebaseDocument._fromFirebase(Task, o)
    }
}

export class ClauseReference {
    @Expose()
    public description: string
    @Expose()
    public number: string
    @Expose()
    public quotation: string
    @Expose()
    public title: string
}

export class DocumentUpdateInstruction {
    @Expose()
    public id: string
    @Expose()
    public update_instructions: string
    @Expose()
    public name?: string
    @Expose()
    public action_type?: string
    @Expose()
    public instructions?: string[]
    @Expose()
    public suggested_content?: string

    get displayName(): string {
        return this.name || this.id
    }
}

/**
 * Read from a Firestore document, so something can be written back to it.
 * False for a task assembled in the browser, like a partner audit's `qa:` rows
 * (`taskFromActionPlanV2`): it is read, never edited, decided or commented
 * (MYAC-1418). A function over `path` rather than a getter, so the plain task
 * shapes the tests mount answer it too.
 */
export function isStoredTask(task: Pick<Task, 'path'> | null | undefined): boolean {
    return !!task?.path
}
